<!--
// Copyright © 2020, 2021 Anticrm Platform Contributors.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
-->
<script lang="ts">
  import { Analytics } from '@hcengineering/analytics'
  import platform, { loadPluginStrings, setMetadata } from '@hcengineering/platform'
  import { onDestroy, onMount, setContext } from 'svelte'
  import { get, writable } from 'svelte/store'
  import { ThemeVariant } from './variants'
  import {
    CUSTOM_THEME_ID,
    ThemeOptions,
    applyCustomThemeColors,
    clearCustomThemeColors,
    customThemeColorStore,
    getCurrentFontSize,
    getCurrentLanguage,
    getCurrentTheme,
    isSystemThemeDark,
    isThemeDark,
    themeStore as themeOptions,
    getCurrentEmoji
  } from './'

  const currentTheme = writable<string>(getCurrentTheme())
  const currentFontSize = writable<string>(getCurrentFontSize())
  const currentLanguage = writable<string>(getCurrentLanguage())
  const currentEmoji = writable<string>(getCurrentEmoji())

  const setLocalStorageItem = (key: string, value: string): void => {
    try {
      globalThis.localStorage?.setItem(key, value)
    } catch {
      // Keep live theme changes working when browser storage is unavailable.
    }
  }

  const setOptions = (currentFont: string, theme: string, language: string, emoji: string) => {
    themeOptions.set(new ThemeOptions(currentFont === 'normal-font' ? 16 : 14, isThemeDark(theme), language, emoji))
  }

  const getRealTheme = (theme: string): string => (isThemeDark(theme) ? ThemeVariant.Dark : ThemeVariant.Light)
  const getThemeClasses = (theme: string, fontSize = getCurrentFontSize(), emoji = getCurrentEmoji()): string => {
    const realTheme = getRealTheme(theme)
    const selectedTheme = theme !== 'theme-system' && theme !== realTheme ? ` ${theme}` : ''
    return `${realTheme}${selectedTheme} ${fontSize} ${emoji}`
  }
  const updateCustomThemeColors = (theme: string): void => {
    if (theme === CUSTOM_THEME_ID) {
      applyCustomThemeColors(get(customThemeColorStore))
    } else {
      clearCustomThemeColors()
    }
  }
  const setRootColors = (theme: string, set = true) => {
    currentTheme.set(theme)
    if (set) {
      setLocalStorageItem('theme', theme)
    }
    document.documentElement.setAttribute('class', getThemeClasses(theme, get(currentFontSize), get(currentEmoji)))
    updateCustomThemeColors(theme)
    setOptions(get(currentFontSize), theme, get(currentLanguage), get(currentEmoji))
  }
  const setRootFontSize = (fontsize: string, set = true) => {
    currentFontSize.set(fontsize)
    if (set) {
      setLocalStorageItem('fontsize', fontsize)
    }
    document.documentElement.setAttribute('class', getThemeClasses(get(currentTheme), fontsize, get(currentEmoji)))
    setOptions(fontsize, get(currentTheme), get(currentLanguage), get(currentEmoji))
  }
  const setLanguage = async (language: string, set: boolean = true) => {
    currentLanguage.set(language)
    if (set) {
      setLocalStorageItem('lang', language)
    }
    Analytics.setTag('language', language)
    setMetadata(platform.metadata.locale, language)
    await loadPluginStrings(language, set)
    setOptions(get(currentFontSize), get(currentTheme), language, get(currentEmoji))
  }
  const setEmoji = (emoji: string, set = true) => {
    currentEmoji.set(emoji)
    if (set) {
      setLocalStorageItem('emoji', emoji)
    }
    document.documentElement.setAttribute('class', getThemeClasses(get(currentTheme), get(currentFontSize), emoji))
    setOptions(get(currentFontSize), get(currentTheme), get(currentLanguage), emoji)
  }

  setContext('theme', {
    currentTheme,
    setTheme: setRootColors
  })
  setContext('fontsize', {
    currentFontSize,
    setFontSize: setRootFontSize
  })
  setContext('lang', {
    currentLanguage,
    setLanguage
  })
  setContext('emoji', {
    currentEmoji,
    setEmoji
  })

  let remove: any = null
  const unsubscribeCustomThemeColors = customThemeColorStore.subscribe((colors) => {
    if (get(currentTheme) === CUSTOM_THEME_ID) {
      applyCustomThemeColors(colors)
    }
  })

  function checkSystemTheme (): void {
    const theme = $currentTheme
    if (remove !== null || theme !== 'theme-system') {
      remove()
      remove = null
    }

    const isDark = isSystemThemeDark()
    const media = matchMedia(`(prefers-color-scheme: ${isDark ? 'light' : 'dark'})`)
    setRootColors(theme)
    media.addEventListener('change', checkSystemTheme)
    remove = () => {
      media.removeEventListener('change', checkSystemTheme)
    }
  }

  $: if ($currentTheme === 'theme-system') {
    checkSystemTheme()
  }

  const setDocumentLanguage = (): void => {
    document.documentElement.lang = $currentLanguage
  }

  onMount(() => {
    setRootColors($currentTheme, false)
    setRootFontSize($currentFontSize, false)
    void setLanguage($currentLanguage, false)
    void loadPluginStrings($currentLanguage)
    setDocumentLanguage()
  })

  onDestroy(() => {
    unsubscribeCustomThemeColors()
  })
</script>

<slot />
